import type { QaBlockResource } from '@next-space/fe-api-idl';
import { QASearchSource } from '@next-space/fe-api-idl';
import type { Placement } from '@popperjs/core';
import Fuse from 'fuse.js';
import type { FC, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { AIModeList } from '../../ai-const';
import { Input } from '../../components/input';
import type { KeyboardItemBase, KeyboardListRef } from '../../components/keyboard-list';
import { KeyboardItemType, KeyboardList } from '../../components/keyboard-list';
import type { PcIconName } from '../../icon-svg';
import { useOpenModal } from '../../next-modal';
import { checkAiSpaceStatus } from '../hooks/ai-limit-store';
import { getEditor } from '../hooks/cache';
import { useConversationContext } from '../hooks/context';
import {
  useChat,
  useChatTools,
  useConversationEdit,
  useConversationTools,
  useSearchPageStore,
} from '../hooks/hook';
import { ReferencePageType, SearchPageStatus } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import { getPageTitleInfo, searchPages } from '../hooks/use-search';
import { ApplySpaceAiButton } from './apply-space-ai';

type ListItem = KeyboardItemBase & {
  sourceType?: QASearchSource;
};

const useSourceItems = () => {
  const { t } = useChatTranslation();
  return [
    {
      id: QASearchSource.ALL,
      title: t('chatAllResources'),
      sourceType: QASearchSource.ALL,
      type: KeyboardItemType.Source,
      iconName: 'IcUser' as PcIconName,
      tooltip: t('chatSourceAllTooltip'),
    },
    {
      id: QASearchSource.SPACE,
      title: t('chatSpace'),
      sourceType: QASearchSource.SPACE,
      type: KeyboardItemType.Source,
      iconName: 'IcSpace' as PcIconName,
      tooltip: t('chatSourceSpaceTooltip'),
    },
    {
      id: QASearchSource.SUBSCRIBE,
      title: t('chatSubscribe'),
      sourceType: QASearchSource.SUBSCRIBE,
      type: KeyboardItemType.Source,
      iconName: 'IcSubscribe' as PcIconName,
      tooltip: t('chatSourceSubscribeTooltip'),
    },
    // {
    //   id: QASearchSource.AI_MODEL,
    //   title: t('chatAIModel'),
    //   sourceType: QASearchSource.AI_MODEL,
    //   type: KeyboardItemType.Source,
    //   iconName: 'IcAiFill' as PcIconName,
    //   iconClassName: 'text-ai_color',
    //   tooltip: t('chatSourceAIModelTooltip'),
    // },
    {
      id: QASearchSource.HELP,
      title: t('chatHelp'),
      sourceType: QASearchSource.HELP,
      type: KeyboardItemType.Source,
      iconName: 'IcHelp' as PcIconName,
      tooltip: t('chatSourceHelpTooltip'),
    },
  ];
};

interface SourcesOptionProps {
  toggleQaSearchSource: (option?: QASearchSource) => void;
  toggleSearchBlocs: (page?: QaBlockResource) => void;
  isSelectedQaSearchSource: (option?: QASearchSource) => boolean;
  isSelectedSearchBlocks: (uuid: string) => boolean;
}

export const SourcesOption: FC<SourcesOptionProps> = (props) => {
  const { t } = useChatTranslation();
  const {
    toggleQaSearchSource,
    toggleSearchBlocs,
    isSelectedQaSearchSource,
    isSelectedSearchBlocks,
  } = props;
  const [keywords, setKeywords] = useState('');
  const { pages, searchStatus } = useSearchPageStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const keyboardListRef = useRef<KeyboardListRef>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sourceItems = useSourceItems();
  const { spaceId, renderPageIcon } = useConversationContext();
  const status = checkAiSpaceStatus(spaceId);

  useEffect(() => {
    if (status) {
      inputRef.current?.focus();
    } else {
      containerRef.current?.focus();
    }
  }, [status]);

  const pagesFuse = useMemo(
    () =>
      new Fuse(Object.values(pages), {
        keys: ['title'],
        threshold: 0.3,
      }),
    [pages]
  );

  const filteredPages = useMemo(
    () =>
      keywords ? pagesFuse.search(keywords).map((result) => result.item) : Object.values(pages),
    [keywords, pages, pagesFuse]
  );

  const formattedPages = useMemo(
    () =>
      filteredPages.map((block) => ({
        id: block.uuid,
        title: getPageTitleInfo(block).title,
        type: KeyboardItemType.Page as const,
        iconName: 'IcPages' as const,
        isMultipleChecked: true,
        renderPageIcon: () => renderPageIcon?.(block),
      })),
    [filteredPages, renderPageIcon]
  );

  // Combine sources and pages
  const listItems = useMemo(() => {
    const restrictedSources = status ? [] : [QASearchSource.SPACE, QASearchSource.SUBSCRIBE];
    const availableItems = sourceItems.filter(
      (item) => !restrictedSources.includes(item.sourceType)
    );
    const restrictedItems = sourceItems.filter((item) =>
      restrictedSources.includes(item.sourceType)
    );

    const items = availableItems.map((item) => ({
      ...item,
      isChecked: isSelectedQaSearchSource(item.sourceType),
    })) as ListItem[];

    if (!status) {
      items.push({
        type: KeyboardItemType.Divider,
      });
      items.push({
        type: KeyboardItemType.Option,
        title: t('chatRestrictedSources'),
        selectable: false,
        renderRight: () => <ApplySpaceAiButton />,
      });
      items.push(
        ...(restrictedItems.map((item) => ({
          ...item,
          isChecked: isSelectedQaSearchSource(item.sourceType),
          disabled: true,
        })) as ListItem[])
      );
    } else {
      if (searchStatus === SearchPageStatus.LOADING) {
        items.push({
          type: KeyboardItemType.Divider,
          title: t('chatSearching'),
        });
      }
      items.push({
        type: KeyboardItemType.Divider,
        title: t('chatSelectPages'),
      });

      const filteredPages = formattedPages.map((page) => ({
        ...page,
        title: page.title,
        isChecked: isSelectedSearchBlocks(page.id),
      }));

      const result = [
        ...items,
        ...filteredPages.filter((page) => page.isChecked),
        ...filteredPages.filter((page) => !page.isChecked),
      ];
      //.slice(0, 10);

      return result;
    }

    return items;
  }, [
    formattedPages,
    searchStatus,
    t,
    sourceItems,
    isSelectedQaSearchSource,
    isSelectedSearchBlocks,
    status,
  ]);

  useEffect(() => {
    if (!status || (!keywords && formattedPages.length)) {
      return;
    }
    void searchPages({
      spaceId,
      query: keywords,
    });
  }, [formattedPages.length, keywords, spaceId, status]);

  const onClickItem = useCallback(
    (item: ListItem) => {
      if (item.type === KeyboardItemType.Divider) return;

      if (item.type === KeyboardItemType.Source && item.sourceType) {
        toggleQaSearchSource(item.sourceType);
      }

      if (item.type === KeyboardItemType.Page && item.id) {
        toggleSearchBlocs({
          uuid: item.id,
          type: ReferencePageType.PAGE,
        });
      }
    },
    [toggleQaSearchSource, toggleSearchBlocs]
  );

  return (
    <div
      className="next-modal py-2 w-96 text-normal"
      ref={containerRef}
      tabIndex={status ? undefined : 0}
      onKeyDown={(e) => {
        if (containerRef.current) {
          // @ts-ignore - Event type mismatch but it works
          keyboardListRef.current?.onKeyDown({ event: e });
        }
      }}
    >
      {status && (
        <div className="px-4 py-1.5">
          <Input
            inputRef={inputRef}
            value={keywords}
            onChange={setKeywords}
            className="py-1"
            placeholder={t('chatSearchPagesPlaceholder')}
            onKeyDown={(e) => {
              // Only pass arrow keys to keyboard list
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
              }
            }}
          />
        </div>
      )}
      <div className="flex flex-col text-t2 overflow-scroll">
        <KeyboardList
          className="max-h-[50vh]"
          ref={keyboardListRef}
          items={listItems}
          onClickItem={onClickItem}
        />
      </div>
    </div>
  );
};

export enum SourcesOptionType {
  CONVERSATION = 'conversation',
  CHAT = 'chat',
}

export const useOpenSourcesOption = (type: SourcesOptionType) => {
  const context = useConversationContext();
  const openModal = useOpenModal();

  return useCallback(
    (event: MouseEvent, opt?: { placement?: Placement }) => {
      const target = event.currentTarget as HTMLElement;
      openModal.dropdown({
        popcorn: target,
        placement: opt?.placement || 'bottom-start',
        offset: [-20, 10],
        content: () =>
          type === SourcesOptionType.CONVERSATION ? <ConversationSources /> : <ChatSources />,
        closeAfterCallBack: () => {
          const editor = getEditor(context.conversationId);
          if (editor) {
            editor.commands.focus();
          }
        },
      });
    },
    [openModal, context, type]
  );
};

const ConversationSources = () => {
  const tools = useConversationTools();
  useConversationEdit();

  return <SourcesOption {...tools} />;
};

const ChatSources = () => {
  const tools = useChatTools();
  useChat();

  return <SourcesOption {...tools} />;
};

interface SelectOptionProps {
  selectedId?: string;
  onSelect: (id: string) => void;
}

const aiModelId = v4();

export const getAIModelItems = (selectedId?: string) => {
  return AIModeList.map((item) => ({
    id: item.value,
    title: item.title,
    type: KeyboardItemType.Source,
    aiModel: true,
    isChecked: item.value === selectedId,
  }));
};

export const AIModelOption: FC<SelectOptionProps> = (props) => {
  const { selectedId, onSelect } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const openModal = useOpenModal();

  useEffect(() => {
    containerRef.current?.focus();
  }, []);
  const listItems = useMemo(() => {
    return getAIModelItems(selectedId);
  }, [selectedId]);

  const onClickItem = useCallback(
    (item: ListItem) => {
      if (item.id) {
        openModal.closeModal(aiModelId);
        onSelect(item.id);
      }
    },
    [onSelect, openModal]
  );

  return <KeyboardList className="max-h-[40vh]" items={listItems} onClickItem={onClickItem} />;
};

export const useOpenSelectAIModel = () => {
  const openModal = useOpenModal();

  return useCallback(
    (event: MouseEvent, props: SelectOptionProps & { placement?: Placement }) => {
      const target = event.currentTarget as HTMLElement;
      openModal.dropdown({
        modalId: aiModelId,
        popcorn: target,
        placement: props.placement || 'bottom-start',
        offset: [-20, 10],
        className: 'next-modal',
        content: () => <AIModelOption {...props} />,
      });
    },
    [openModal]
  );
};
